import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { fetchSubscription } from '../lib/apiClient';
import { queryOrNull } from '../lib/query/queryFn';
import { subscriptionKeys } from '../lib/query/subscriptionKeys';
import type { Subscription } from '../types';

// Shared subscription query (stories 2-20, 2-32, 2-54, 2-73): every consumer of
// the org's Subscription doc — Sidebar's plan card, Dashboard, Billing, and the
// public Pricing page — shares the exact `subscriptionKeys.detail(orgId)` cache
// key (`lib/query/subscriptionKeys.ts`), so they MUST also share one queryFn or
// they drift out of sync one at a time (2-32 fixed Dashboard/Billing/Sidebar;
// 2-54 found Pricing had been missed).
//
// A missing subscription doc (e.g. an owner-provisioned org before its plan
// lands) 404s. That is a valid "no subscription" state, not an error, so this
// routes through the shared `queryOrNull` helper (`lib/query/queryFn.ts`, G-14/
// G-20) instead of the hand-rolled `try/catch(404)` this file inlined before
// 2-73 — the exact inline check the sweep's own eslint selector flags
// (`.eslintrc.cjs`'s G-14/G-20 `no-restricted-syntax` entry).
export function useSubscription(
  organizationId: string | undefined,
  enabled = true
): UseQueryResult<Subscription | null> {
  return useQuery<Subscription | null>({
    queryKey: subscriptionKeys.detail(organizationId),
    queryFn: () => {
      if (!organizationId) return Promise.resolve(null);
      return queryOrNull(fetchSubscription(organizationId));
    },
    enabled: Boolean(organizationId) && enabled,
    staleTime: 1000 * 60 * 5,
    retry: false,
  });
}
